import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, GraduationCap, Loader2, RotateCcw, Sparkles, Download, FileText, Presentation, Lock } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import FloatingShapes from "@/components/FloatingShapes";
import ShareMenu from "@/components/ShareMenu";
import AdsterraBanner from "@/components/AdsterraBanner";
import api, { downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Adsterra ad units — shown to guest and free-tier users
const AD_NATIVE = `<script async="async" data-cfasync="false" src="https://bauval.org/21/00c8a7d4609b2d904bade74547ab57e6"></script><div id="container-00c8a7d4609b2d904bade74547ab57e6"></div>`;
const AD_728x90 = `<script>atOptions = {'key':'9cf67bec3b36ad7fc9e6db318733cf1c','format':'iframe','height':90,'width':728,'params':{}}</script><script src="https://bauval.org/22/9cf67bec3b36ad7fc9e6db318733cf1c"></script>`;
const AD_300x250 = `<script>atOptions = {'key':'c78ee766345813ed7d53839c360c3ddd','format':'iframe','height':250,'width':300,'params':{}}</script><script src="https://bauval.org/22/c78ee766345813ed7d53839c360c3ddd"></script>`;

const PAID_TIERS = ["pro_monthly", "pro_annual", "lifetime"];
// Card dimensions — identical to SharedDeck so layout is consistent everywhere
const CARD_HEIGHT = 600;

const GUEST_DECKS_KEY = "loom_guest_deck_ids";
function getGuestDeckIds() {
  try { return JSON.parse(localStorage.getItem(GUEST_DECKS_KEY) || "[]"); } catch { return []; }
}

export default function Viewer() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [active, setActive] = useState(0);
  const scrollerRef = useRef(null);
  // Navigation hint — shown once on first load, dismissed on navigation or after 5s
  const [showNavHint, setShowNavHint] = useState(true);
  // Language choice modal
  const [showLangModal, setShowLangModal] = useState(false);
  const [langChoice, setLangChoice] = useState(null); // "keep" | "translate"

  // Guest session: determine deck position from localStorage
  const isGuest = !user;
  const guestDeckIds = isGuest ? getGuestDeckIds() : [];
  const guestDeckIndex = guestDeckIds.indexOf(id); // 0-based; -1 if not found (fallback: show full)
  const isSecondGuestDeck = isGuest && guestDeckIndex >= 4; // 5th+ guest deck shows 50%

  // Show ads to guests and free-tier logged-in users; hide for pro/lifetime
  const isActuallyPaid = !!(user && PAID_TIERS.includes(user.tier));
  const showAds = !isActuallyPaid;
  // Export capability: paid users or shared library members
  const isPaid = !!user;

  const load = async () => {
    try {
      const r = await api.get(`/documents/${id}`);
      setDoc(r.data);
      if ((r.data.locked_sections || 0) > 0) {
        api.post("/events/paywall", { feature: "sections", kind: "view", doc_id: id }).catch(()=>{});
      }
      if (r.data.status === "processing" || r.data.status === "uploaded") {
        setTimeout(load, 3500);
      } else if (r.data.status === "language_pending") {
        setShowLangModal(true);
        // Don't re-poll — wait for user action
      }
    } catch { toast.error("Couldn't load this deck"); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Auto-dismiss the navigation hint after 5 seconds
  useEffect(() => {
    if (!showNavHint) return;
    const t = setTimeout(() => setShowNavHint(false), 5000);
    return () => clearTimeout(t);
  }, [showNavHint]);

  const handleLanguageChoice = async (translate) => {
    setLangChoice(translate ? "translate" : "keep");
    setShowLangModal(false);
    try {
      await api.post(`/documents/${id}/set-language`, { translate });
      setDoc(d => ({ ...d, status: "processing" }));
      setTimeout(load, 2000); // Start polling
    } catch (e) {
      toast.error("Couldn't save your preference. Please try again.");
      setShowLangModal(true);
      setLangChoice(null);
    }
  };

  const canExport = isPaid || doc?.shared;

  // Guest 2nd-deck lock: show first 50% of sections, lock the rest
  const rawSections = doc?.sections || [];
  let displaySections = rawSections;
  let guestLockedCount = 0;
  if (isSecondGuestDeck && rawSections.length > 0) {
    const keep = Math.max(1, Math.floor(rawSections.length * 0.5));
    displaySections = rawSections.slice(0, keep);
    guestLockedCount = rawSections.length - keep;
  }

  const downloadDeck = async (fmt) => {
    const t = toast.loading(`Preparing ${fmt.toUpperCase()}…`);
    try {
      await downloadFile(`/documents/${doc.doc_id}/export?fmt=${fmt}`,
                         `${(doc.name || "deck").replace(/\.pdf$/i, "")}.${fmt === "pptx" ? "pptx" : "pdf"}`);
      toast.success(`Downloaded ${fmt.toUpperCase()}`, { id: t });
    } catch (e) {
      toast.error(e?.message || "Export failed", { id: t });
    }
  };

  const scrollTo = (idx) => {
    setShowNavHint(false); // dismiss hint on any navigation
    if (!scrollerRef.current) return;
    const el = scrollerRef.current.querySelectorAll("[data-scroll-card]")[idx];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  };

  useEffect(() => {
    const sc = scrollerRef.current;
    if (!sc) return;
    const onScroll = () => {
      const cards = sc.querySelectorAll("[data-scroll-card]");
      const center = sc.scrollLeft + sc.clientWidth / 2;
      let best = 0, bestD = Infinity;
      cards.forEach((c, i) => {
        const mid = c.offsetLeft + c.offsetWidth/2;
        const d = Math.abs(mid - center);
        if (d < bestD) { bestD = d; best = i; }
      });
      setActive(best);
    };
    sc.addEventListener("scroll", onScroll, { passive: true });
    return () => sc.removeEventListener("scroll", onScroll);
  }, [doc]);

  // Prime the scroller once cards are painted — a tiny programmatic nudge wakes up
  // Android Chrome's touch-scroll handler (scroll-snap can otherwise ignore the
  // first touch-drag until a programmatic scroll happens).
  useEffect(() => {
    if (doc?.status !== "ready") return;
    const sc = scrollerRef.current;
    if (!sc) return;
    const t = setTimeout(() => {
      sc.scrollTo({ left: 1, behavior: "auto" });
      requestAnimationFrame(() => sc.scrollTo({ left: 0, behavior: "auto" }));
    }, 250);
    return () => clearTimeout(t);
  }, [doc]);

  if (!doc) {
    return (
      <div className="min-h-screen flex items-center justify-center font-hand text-3xl text-stone-500">
        unfurling…
      </div>
    );
  }

  if (doc.status !== "ready") {
    return (
      <div className="relative min-h-screen grain" data-testid="viewer-loading">
        <FloatingShapes/><Nav/>
        <main className="relative z-10 max-w-3xl mx-auto px-6 pt-40 text-center">
          {doc.status === "language_pending" ? (
            <>
              <div className="text-5xl mb-6">🌐</div>
              <h1 className="font-display text-4xl">One quick thing</h1>
              <p className="font-hand text-stone-500 text-2xl mt-2">{doc.name}</p>
              <p className="text-stone-600 mt-4 max-w-md mx-auto">
                We detected this document is in <strong>{doc.detected_language || "a non-English language"}</strong>.
                How would you like to proceed?
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => handleLanguageChoice(false)}
                  className="btn-ghost text-base py-3 px-8 border border-stone-200"
                  data-testid="lang-keep-btn"
                  disabled={!!langChoice}
                >
                  Keep {doc.detected_language || "original language"}
                </button>
                <button
                  onClick={() => handleLanguageChoice(true)}
                  className="btn-primary text-base py-3 px-8"
                  data-testid="lang-translate-btn"
                  disabled={!!langChoice}
                >
                  Translate to English
                </button>
              </div>
              {langChoice && (
                <div className="mt-6 flex items-center justify-center gap-2 text-stone-500">
                  <Loader2 className="w-5 h-5 animate-spin"/>
                  <span>Processing your document…</span>
                </div>
              )}
            </>
          ) : (
            <>
              <Loader2 className="w-10 h-10 animate-spin text-indigo-500 mx-auto"/>
              <h1 className="font-display text-4xl mt-6">Sketching your sections</h1>
              <p className="font-hand text-stone-500 text-2xl mt-2">{doc.name}</p>
              <p className="text-stone-600 mt-6">
                The model is reading the document and finding its joints. This usually takes 20–60 seconds.
              </p>
            </>
          )}
          {doc.status === "failed" && (
            <div className="mt-6 text-red-600">⚠ {doc.error}</div>
          )}
        </main>
      </div>
    );
  }

  const sections = displaySections;
  const hasLocked = (doc.locked_sections || 0) > 0 || guestLockedCount > 0;
  const totalLockedSections = (doc.locked_sections || 0) + guestLockedCount;
  const totalCards = sections.length + (hasLocked ? 1 : 0);

  return (
    <div className="relative min-h-screen grain overflow-x-hidden" data-testid="viewer-page">
      <FloatingShapes/><Nav/>
      <main className="relative z-10 pt-20 sm:pt-24 pb-28 max-w-6xl mx-auto px-6">

        {/* ── Compact header: max-height 80px, title 1.2rem ── */}
        <div
          className="mt-4 overflow-hidden"
          style={{ maxHeight: "80px" }}
          data-testid="viewer-header"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3">
                {isPaid && (
                  <Link to="/dashboard" className="text-xs text-stone-400 hover:text-stone-700 inline-flex items-center gap-1 shrink-0" data-testid="back-to-dashboard">
                    <ArrowLeft className="w-3 h-3"/> Decks
                  </Link>
                )}
                {isGuest && (
                  <Link to="/" className="text-xs text-stone-400 hover:text-stone-700 inline-flex items-center gap-1 shrink-0" data-testid="back-to-home">
                    <ArrowLeft className="w-3 h-3"/> Home
                  </Link>
                )}
                <span className="font-hand text-stone-400 text-xs shrink-0">{sections.length} rooms · scroll →</span>
              </div>
              <h1
                className="font-display leading-snug mt-0.5 truncate"
                style={{ fontSize: "1.2rem" }}
                title={doc.doc_title || doc.name}
                data-testid="viewer-title"
              >
                {doc.doc_title || doc.name}
              </h1>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {canExport ? (
                <>
                  <button onClick={()=>downloadDeck("pdf")} className="btn-ghost inline-flex items-center gap-1 text-sm py-1.5 px-2.5" data-testid="export-deck-pdf-button">
                    <FileText className="w-3.5 h-3.5"/> PDF
                  </button>
                  <button onClick={()=>downloadDeck("pptx")} className="btn-ghost inline-flex items-center gap-1 text-sm py-1.5 px-2.5" data-testid="export-deck-pptx-button">
                    <Presentation className="w-3.5 h-3.5"/> PPTX
                  </button>
                </>
              ) : isGuest ? (
                <>
                  <button onClick={()=>nav("/pricing")} className="btn-ghost inline-flex items-center gap-1 text-sm py-1.5 px-2.5 opacity-60" data-testid="export-deck-pdf-button">
                    <Lock className="w-3.5 h-3.5"/> PDF
                  </button>
                  <button onClick={()=>nav("/pricing")} className="btn-ghost inline-flex items-center gap-1 text-sm py-1.5 px-2.5 opacity-60" data-testid="export-deck-pptx-button">
                    <Lock className="w-3.5 h-3.5"/> PPTX
                  </button>
                </>
              ) : (
                <>
                  <button onClick={()=>{ api.post("/events/paywall",{feature:"export",kind:"upgrade_click",doc_id:doc.doc_id}).catch(()=>{}); nav("/pricing"); }} className="btn-ghost inline-flex items-center gap-1 text-sm py-1.5 px-2.5 opacity-80" data-testid="export-deck-pdf-button">
                    <FileText className="w-3.5 h-3.5"/> PDF
                  </button>
                  <button onClick={()=>{ api.post("/events/paywall",{feature:"export",kind:"upgrade_click",doc_id:doc.doc_id}).catch(()=>{}); nav("/pricing"); }} className="btn-ghost inline-flex items-center gap-1 text-sm py-1.5 px-2.5 opacity-80" data-testid="export-deck-pptx-button">
                    <Presentation className="w-3.5 h-3.5"/> PPTX
                  </button>
                </>
              )}
              <button onClick={()=>nav(`/quiz/${doc.doc_id}`)} className="btn-primary inline-flex items-center gap-1 text-sm py-1.5 px-2.5" data-testid="open-quiz-button">
                <GraduationCap className="w-3.5 h-3.5"/> Quiz me
              </button>
              {!isGuest && !doc.shared && <ShareMenu docId={doc.doc_id} docName={doc.name} />}
            </div>
          </div>
          {doc.shared && (
            <span className="inline-flex items-center gap-1.5 mt-1 text-xs uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700" data-testid="viewer-shared-badge">
              <Sparkles className="w-3 h-3"/> shared by {doc.shared_from}
            </span>
          )}
        </div>

        {/* ── Horizontal card scroll — fixed 480×600px cards ── */}
        <div ref={scrollerRef} className="h-scroll mt-4" data-testid="horizontal-scroll">
          {sections.map((s, i) => (
            <div
              key={s.id}
              data-scroll-card
              data-testid={`section-card-${i}`}
              className="snap-card w-[90vw] lg:w-[480px]"
              style={{ height: `${CARD_HEIGHT}px`, flexShrink: 0 }}
            >
            <motion.article
              data-section-card
              initial={{ opacity: 0, y: 30, rotate: (i%2 ? 1.2 : -1.2) }}
              animate={{ opacity: 1, y: 0, rotate: (i%2 ? 0.6 : -0.6) }}
              transition={{ delay: i * 0.05, duration: 0.7 }}
              className="sticky-card relative overflow-y-auto overflow-x-hidden p-6"
              style={{
                height: "100%",
                background: `linear-gradient(180deg, ${s.color}55 0%, #ffffff 60%)`
              }}
            >
              <div className="flex items-center justify-between">
                <span className="tag-pill" data-testid={`section-index-${i}`}>{String(i+1).padStart(2,"0")} / {String(sections.length).padStart(2,"0")}</span>
                {s.page_range && <span className="font-hand text-stone-500 text-sm">p. {s.page_range}</span>}
              </div>
              <h2 className="font-display text-2xl mt-4 leading-tight">{s.title}</h2>
              {s.subtitle && <div className="font-hand text-stone-600 text-lg mt-1">{s.subtitle}</div>}
              <p className="text-stone-700 mt-4 leading-relaxed text-sm">{s.summary}</p>
              {s.key_points?.length > 0 && (
                <ul className="mt-4 space-y-1.5">
                  {s.key_points.map((k, j) => (
                    <li key={j} className="flex gap-2 text-stone-800 text-sm">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 shrink-0"/>
                      <span><span className="marker">{k}</span></span>
                    </li>
                  ))}
                </ul>
              )}
              {s.details && (
                <details className="mt-4 group">
                  <summary className="cursor-pointer text-xs uppercase tracking-[0.18em] text-indigo-600">Deep recall ↓</summary>
                  <p className="text-stone-700 leading-relaxed mt-2 text-xs">{s.details}</p>
                </details>
              )}
              <button
                onClick={()=>nav(`/quiz/${doc.doc_id}?section=${s.id}`)}
                className="mt-6 inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 text-sm"
                data-testid={`quiz-section-${i}`}
              >
                <Sparkles className="w-4 h-4"/> Quiz this section
              </button>
            </motion.article>
            </div>
          ))}
          {hasLocked && (
            <div
              className="snap-card w-[90vw] lg:w-[480px] sticky-card flex flex-col items-center justify-center text-center p-8"
              data-scroll-card
              style={{ height: `${CARD_HEIGHT}px`, flexShrink: 0, background: "linear-gradient(180deg,#FEF08A55 0%,#fff 60%)" }}
              data-testid="locked-sections-card"
            >
              <span className="tag-pill bg-amber-200 text-amber-800">{totalLockedSections} rooms locked</span>
              {isGuest ? (
                <>
                  <h2 className="font-display text-3xl mt-5 leading-tight">You've seen the preview.</h2>
                  <p className="font-hand text-stone-500 text-xl mt-2">Unlock all {totalLockedSections} remaining rooms — it's everything you need.</p>
                  <div className="mt-6 p-4 rounded-2xl bg-indigo-50 border border-indigo-200 text-left w-full">
                    <p className="text-sm text-indigo-700 font-semibold">Pro unlocks</p>
                    <ul className="mt-2 space-y-1 text-sm text-stone-700">
                      <li>✓ All sections of every deck</li>
                      <li>✓ Unlimited uploads</li>
                      <li>✓ PDF & PPTX export</li>
                      <li>✓ No ads, ever</li>
                    </ul>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 mt-6 w-full">
                    <button onClick={()=>nav("/pricing")} className="btn-primary flex-1 text-base py-3" data-testid="locked-upgrade-btn">
                      Unlock now →
                    </button>
                    <Link to="/register" className="btn-ghost flex-1 text-base py-3" data-testid="locked-signup-btn">
                      Create free account
                    </Link>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="font-display text-3xl mt-5 leading-tight">This is your 5th deck.</h2>
                  <p className="font-hand text-stone-600 text-xl mt-2">You've unlocked the full potential — now go Pro to keep everything.</p>
                  <div className="mt-6 p-4 rounded-2xl bg-indigo-50 border border-indigo-200 text-left w-full">
                    <p className="text-sm text-indigo-700 font-semibold">You're missing {totalLockedSections} study rooms</p>
                    <ul className="mt-2 space-y-1 text-sm text-stone-700">
                      <li>✓ Full access to every section</li>
                      <li>✓ Unlimited new decks (no 5-deck ceiling)</li>
                      <li>✓ PDF & PPTX export</li>
                      <li>✓ No ads on shared decks</li>
                    </ul>
                  </div>
                  <button
                    onClick={()=>{ api.post("/events/paywall", { feature: "sections", kind: "upgrade_click", doc_id: id }).catch(()=>{}); nav("/pricing"); }}
                    className="btn-primary w-full text-base py-3 mt-6"
                    data-testid="locked-upgrade-btn"
                  >
                    Upgrade to Pro — Unlock Everything →
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* ── Ads: scaled to 25% of original size (75% reduction), below scroll ── */}
        {showAds && (
          <div
            className="flex flex-col items-center gap-1"
            style={{ marginTop: "12px" }}
            data-testid="viewer-ad-container"
          >
            <div style={{ zoom: 0.25 }} data-testid="viewer-ad-native">
              <AdsterraBanner html={AD_NATIVE} />
            </div>
            <div style={{ zoom: 0.25 }} data-testid="viewer-ad-728">
              <AdsterraBanner html={AD_728x90} />
            </div>
            <div style={{ zoom: 0.25 }} data-testid="viewer-ad-300">
              <AdsterraBanner html={AD_300x250} />
            </div>
          </div>
        )}

      </main>

      {/* Navigation hint — shown once on first load, auto-dismisses */}
      <AnimatePresence>
        {showNavHint && doc?.status === "ready" && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.35 }}
            className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 pointer-events-none"
            data-testid="nav-hint"
          >
            <div className="bg-stone-900/85 text-white text-sm px-5 py-2.5 rounded-full flex items-center gap-2 shadow-xl backdrop-blur-sm">
              <ArrowRight className="w-4 h-4 shrink-0 text-indigo-300" />
              <span>Press <kbd className="font-mono bg-white/20 px-1.5 py-0.5 rounded text-xs">→</kbd> or swipe to release Sections</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* minimap — fixed at bottom, always visible */}
      <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 bg-white/80 backdrop-blur-xl px-5 py-3 rounded-full shadow-lg border border-stone-200" data-testid="minimap">
        <button onClick={()=>scrollTo(Math.max(0, active-1))} className="text-stone-500 hover:text-stone-900" data-testid="minimap-prev"><ArrowLeft className="w-4 h-4"/></button>
        <div className="flex items-center gap-1.5 px-1">
          {Array.from({ length: totalCards }).map((_, i) => {
            const isLockedDot = hasLocked && i === totalCards - 1;
            return (
              <button key={i} onClick={()=>scrollTo(i)} className="rounded-full transition-all"
                      title={isLockedDot ? "Unlock the rest" : undefined}
                      style={{
                        width: i===active ? 22 : 8, height: 8,
                        background: i===active ? "#6366F1" : (isLockedDot ? "#FCD34D" : "#d6d3d1")
                      }}
                      data-testid={`minimap-dot-${i}`}/>
            );
          })}
        </div>
        <span className="text-xs text-stone-500 px-2 font-hand text-lg">{active+1}/{totalCards}</span>
        <button onClick={()=>scrollTo(Math.min(totalCards-1, active+1))} className="text-stone-500 hover:text-stone-900" data-testid="minimap-next"><ArrowRight className="w-4 h-4"/></button>
      </div>
    </div>
  );
}
